import { Defer } from '@cian/valuation-utils-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestPushSettingModal } from './requestPushSettingsModal';
import { requestAuthentication } from '../../../actions/authentication';
import { trackReturnedFromPushSystemSettings } from '../../../analytics';
import { selectIsWebview, selectPushNotificationEnabled } from '../../../selectors/page';
import { getUser } from '../../../selectors/user';
import { TThunkDispatch } from '../../../types/redux';
import { useApplicationContext } from '../../../utils/applicationContext';

/**
 * Хук для вызова в режиме вебвью при необходимости совершить подписку на push уведомления
 * Вызывает модалку для перехода в настройки пушей, возвращает промис, чтоб извне дожидаться результата
 * */
export const useRunBeforePushSubscription = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { config } = useApplicationContext();
  const isWebview = useSelector(selectIsWebview);
  const { isAuthenticated } = useSelector(getUser);
  const requestPushAllowFeatureEnabled = config.get('requestPushAllowFeatureEnabled.Enabled');
  const nativePushNotificationAllowed = useSelector(selectPushNotificationEnabled);

  return React.useCallback(async () => {
    /** Ожидание включения/пропуска настройки push-уведомлений и авторизации */
    const pushNotifyAllowedDefer = new Defer<boolean>();
    const authenticationDefer = new Defer<boolean>();

    /** Выполнение авторизации, если требуется  */
    if (!isAuthenticated) {
      dispatch(
        requestAuthentication({
          onSuccess: () => authenticationDefer.resolve(true),
          onCloseNoAuth: () => authenticationDefer.resolve(false),
        }),
      );
    } else {
      authenticationDefer.resolve(true);
    }

    const authPassed = await authenticationDefer.promise;

    if (!authPassed) {
      return false;
    }

    if (isWebview && requestPushAllowFeatureEnabled && !nativePushNotificationAllowed) {
      /**
       * Дожидаемся, когда юзер пройдет модалку "Включить уведомления в настройках", нажав одну из двух кнопок в ней
       * Если нажать "Включить", то откроются настройки телефона, и код будет ждать события из аппки, после чего пойдет дальше
       * */
      dispatch(
        requestPushSettingModal({
          onSkip: () => {
            trackReturnedFromPushSystemSettings(false);
            pushNotifyAllowedDefer.resolve(false);
          },
          onAllow: () => {
            trackReturnedFromPushSystemSettings(true);
            pushNotifyAllowedDefer.resolve(true);
          },
        }),
      );
    } else {
      pushNotifyAllowedDefer.resolve(true);
    }

    return pushNotifyAllowedDefer.promise;
  }, [dispatch, isAuthenticated, isWebview, nativePushNotificationAllowed, requestPushAllowFeatureEnabled]);
};
